import { StatusComponent } from '@efexteam/components';
import { Tooltip } from '@mui/material';
import { capitalizeFirst } from '@utils/formater';

const DUE_DATE_STATES = {
  completed: 'Completed' as const,
  deposited: 'Deposited' as const,
  processing: 'Processing' as const,
  pending_to_process: 'Pending_to_process' as const,
  pending_to_process_scheduled_payment:
    'Pending_to_process_scheduled_payment' as const,
  pending_to_deposit: 'Pending_to_deposit' as const,
  pending: 'Pending' as const,
  not_processed: 'Not_processed' as const,
  missing: 'Missing' as const,
  delivered: 'Delivered' as const,
  regular: 'Regular' as const,
  rejected: 'Rejected' as const,
  expired: 'Expired' as const,
  waiting_for_deposit: 'Waiting_for_deposit' as const,
  validation_amount: 'Validation_amount' as const,
  new_payment_created: 'New_payment_created' as const,
  deposit_failed: 'Deposit_failed' as const,
  balance_discounted: 'Balance_discounted' as const,
  refunded: 'Refunded' as const,
  provider_processing: 'Provider_processing' as const,
  pending_to_approve_by_user: 'Pending_to_approve_by_user' as const,
  approval_time_expired: 'Approval_time_expired' as const,
};

type DueDateLabel = (typeof DUE_DATE_STATES)[keyof typeof DUE_DATE_STATES];

const getStatusVariant = (
  status: DueDateLabel
): 'positive' | 'warning' | 'negative' | 'info' | 'special' => {
  switch (status) {
    case 'Completed':
    case 'Deposited':
    case 'Delivered':
      return 'positive';
    case 'Processing':
    case 'Pending_to_process':
    case 'Pending_to_deposit':
    case 'Pending':
    case 'Waiting_for_deposit':
    case 'Validation_amount':
    case 'New_payment_created':
      return 'warning';
    case 'Not_processed':
    case 'Missing':
    case 'Rejected':
    case 'Expired':
    case 'Deposit_failed':
    case 'Balance_discounted':
    case 'Refunded':
    case 'Approval_time_expired':
      return 'negative';
    case 'Regular':
      return 'info';
    case 'Pending_to_approve_by_user':
    case 'Pending_to_process_scheduled_payment':
    case 'Provider_processing':
      return 'special';
    default:
      return 'info';
  }
};

interface DueDateSpanProps {
  state: string;
  value: string;
  label?: string;
}

const DueDateSpan = ({ state, value, label }: DueDateSpanProps) => {
  const stateValue = capitalizeFirst(state) as DueDateLabel;

  if (
    !stateValue ||
    !DUE_DATE_STATES[stateValue.toLowerCase() as keyof typeof DUE_DATE_STATES]
  ) {
    return <span>{value}</span>;
  }

  if (!label) {
    return (
      <StatusComponent
        displayVariant="chip"
        size="small"
        status={getStatusVariant(stateValue)}
        label={value}
        aria-label={label || value}
      />
    );
  }

  return (
    <Tooltip title={label || value} placement="top">
      <div>
        <StatusComponent
          displayVariant="chip"
          size="small"
          status={getStatusVariant(stateValue)}
          label={value}
          aria-label={label || value}
        />
      </div>
    </Tooltip>
  );
};

export default DueDateSpan;
